// frontend/app/ManageStudentsScreen.js

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  StyleSheet,
  Alert,
  ActivityIndicator,
  Platform,
} from "react-native";
import { useAuth } from "./AuthContext";
import { FASTAPI_BASE_URL } from "./config"; // ensure correct path
import { useTranslation } from "./i18n/useTranslation";
import { MaterialIcons, MaterialCommunityIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";
import { canUseTeacherTools, canBulkCreateInstituteUsers } from "./roles";
import { NotificationProvider, useNotification } from "./NotificationContext";
import { validateStudentRows, hasStartedRow, reindexRowErrorsAfterRemoval } from "./utils/validateStudentRows";

export { validateStudentRows };

const SCHOOL_SWITCH_DEBOUNCE_MS = 250;
const isAbortError = (e) => e?.name === "AbortError";

// Current convention for AI Tutor default password:
const DEFAULT_PASSWORD = "PatAva2022!";

function extractInstituteCode(email) {
  if (!email) return null;
  // e.g. "00008.s001+2@student.noemail.saras3d" -> "00008"
  const m = email.match(/^(\d{5})\./);
  return m ? m[1] : null;
}

function normalizeRoll(roll) {
  return (roll || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");
}

// BDD 01/07/2026 
// const canManageStudents = (role) => {
//   const r = (role || "").toLowerCase();
//   return (
//     r === "teacher" ||
//     r === "admin" ||
//     r === "institute_admin" ||
//     r === "superadmin"
//   );
// };

const emptyRow = () => ({
  fname: "",
  lname: "",
  grade_no: "",
  contact_number: "",
  country_code: "+1",
  gender: "",
  roll_number: "",
});

// hshah - password dialog with guidline and regex check
// rules: min 6 length with atleast 1 special character or digit
const PASSWORD_REGEX = /^(?=.*[\d\W]).{6,}$/;
function validatePassword(pwd, t = (s) => s) {
  if (!pwd || pwd.length < 6) {
    return t("Password must be at least 6 characters");
  }
  if (!PASSWORD_REGEX.test(pwd)) {
    return t("Password must include at least one number or symbol");
  }
  return "";
}

export default function ManageStudentsScreen({
  isDarkMode,
  schools = [],
  canViewAllClasses = false,
  userSchool = null,
  userSchoolId = null,
  onFetchSchools = null,
}) {
  const dk = isDarkMode;
  const { user, fetchWithAuth, contentDb } = useAuth();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const teacherEmail = user?.email || "";
  const instCode = extractInstituteCode(teacherEmail);
  const styles = getStyles(dk);

  // const allowed = canManageStudents(user?.role);
  // BDD 01/07/2026
  // Prefer the backend-computed flag; fallback to shared helper if missing.
  const allowed =
    (user?.can_use_teacher_tools ?? canUseTeacherTools(user?.role)) === true;

  // AIT-298: the backend only lets some roles create student accounts
  // (schooladmin cannot). Prefer the backend flag; fall back to the helper.
  const canBulkCreate =
    (user?.can_bulk_create_users ?? canBulkCreateInstituteUsers(user?.role)) === true;

  // School selector state (for users who can view all classes)
  const [selectedSchoolId, setSelectedSchoolId] = useState(userSchoolId);
  const [selectedSchoolName, setSelectedSchoolName] = useState(userSchool);

  // Fetch schools list on mount if user has multi-school access (AIT-229)
  useEffect(() => {
    if (canViewAllClasses && onFetchSchools && schools.length === 0) {
      onFetchSchools();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced copy of selectedSchoolId: the dropdown updates instantly, the
  // class/student loads follow 250 ms later (AIT-240). schoolLoadTick forces
  // a reload even if the user switched away and back to the same school.
  const [loadSchoolId, setLoadSchoolId] = useState(userSchoolId);
  const [schoolLoadTick, setSchoolLoadTick] = useState(0);
  const schoolDebounceRef = useRef(null);
  const [loadingClasses, setLoadingClasses] = useState(false);

  const [rows, setRows] = useState([emptyRow()]);
  const [rowErrors, setRowErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [backendMessage, setBackendMessage] = useState("");
  const [createdSummaries, setCreatedSummaries] = useState([]);

  // NEW: classes + roster state
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [newClassName, setNewClassName] = useState("");
  const [classSaving, setClassSaving] = useState(false);
  const [classError, setClassError] = useState("");
  const [classRenameValue, setClassRenameValue] = useState("");
  const [classRenameSaving, setClassRenameSaving] = useState(false);

  const [classCollections, setClassCollections] = useState([]);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [updatingCollectionId, setUpdatingCollectionId] = useState(null);
  const [collectionError, setCollectionError] = useState("");

  const [existingEmail, setExistingEmail] = useState("");

  // Request-id counters guard async setState against rapid school/class
  // switching. Each loader bumps its counter and captures the value; any
  // response that lands after a newer request is silently dropped.
  // Without this, slow responses for a previous school can overwrite the
  // UI for the school the user has already moved to.
  const classesLoadIdRef = useRef(0);
  const rosterLoadIdRef = useRef(0);
  const collectionsLoadIdRef = useRef(0);
  // One AbortController per kind of load; starting a new load aborts the
  // previous one (the request-id guards above remain as a second safety net).
  const classesAbortRef = useRef(null);
  const rosterAbortRef = useRef(null);
  const collectionsAbortRef = useRef(null);

  const startAbortable = (ref) => {
    if (ref.current) ref.current.abort();
    const controller = new AbortController();
    ref.current = controller;
    return controller.signal;
  };
  const abortInFlightLoads = () => {
    [classesAbortRef, rosterAbortRef, collectionsAbortRef].forEach((ref) => {
      if (ref.current) ref.current.abort();
    });
  };

  useEffect(
    () => () => {
      if (schoolDebounceRef.current) clearTimeout(schoolDebounceRef.current);
      [classesAbortRef, rosterAbortRef, collectionsAbortRef].forEach((ref) => {
        if (ref.current) ref.current.abort();
      });
    },
    []
  );

  // Latest selectedClassId mirrored into a ref so loadClassesAndRoster can
  // read it without listing it as a useCallback dependency. With
  // selectedClassId in the dep array, every class switch re-creates the
  // callback, which re-fires the mount-time useEffect that depends on it —
  // a hidden second load cycle on every school change.
  const selectedClassIdRef = useRef(null);

  const filteredRoster = React.useMemo(() => {
    const q = (existingEmail || "").trim().toLowerCase();
    if (!q) return roster;
    return roster.filter((s) => {
      const name = (s?.name || "").toLowerCase();
      const email = (s?.email || "").toLowerCase();
      const loginId = ((s?.loginId ?? s?.login_id) || "").toString().toLowerCase();
      return name.includes(q) || email.includes(q) || loginId.includes(q);
    });
  }, [roster, existingEmail]);
  // console.log("[ManageStudents] FE user:", user);

  //hshah - FOR PASSWORD GUIDELINES AND INTERACTIVE DIALOG
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordTargetEmail, setPasswordTargetEmail] = useState(null);
  const [passwordValue, setPasswordValue] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { showNotification } = useNotification();
  const [passwordMode, setPasswordMode] = useState("student");
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  const updateRow = (index, patch) => {
    if (patch.grade_no !== undefined) {
      setRowErrors((prev) => {
        if (!prev[index]) return prev;
        const { [index]: _removed, ...rest } = prev;
        return rest;
      });
    }
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setRowErrors((prev) => reindexRowErrorsAfterRemoval(prev, index));
  };

  const buildUsersPayload = () => {
    const currentYear = new Date().getFullYear();

    return rows
      .map((r, i) => {
        const fname = (r.fname || "").trim();
        const lname = (r.lname || "").trim();
        const grade_no = parseInt(r.grade_no, 10);
        const contact = (r.contact_number || "").trim();

        // allow visually empty rows; filter later
        if (!fname && !contact && !grade_no) return null;

        return {
          role: "student",
          fname,
          lname,
          grade_no: isNaN(grade_no) ? null : grade_no,
          email: null, // FastAPI/Django will generate synthetic email
          country_code: r.country_code || "+1",
          contact_number: contact || null,
          gender: (r.gender || "").toLowerCase().startsWith("f")
            ? "f"
            : (r.gender || "").toLowerCase().startsWith("m")
            ? "m"
            : "",
          current_year: currentYear,
          roll_number:
            (r.roll_number || "").trim() ||
            `S-${String(i + 1).padStart(3, "0")}`,
        };
      })
      .filter(
        (u) =>
          u &&
          u.fname &&
          u.grade_no
      );
  };

  // BDD 11/9/2025
  const handleAddExistingToClass = async () => {
  if (!selectedClassId) {
    Alert.alert(t("Select class"), t("Please select a class first."));
    return;
  }
  const email = (existingEmail || "").trim();
  if (!email) {
    Alert.alert(t("Missing email"), t("Enter an existing student's login ID/email."));
    return;
  }

  try {
    const res = await fetchWithAuth(
      `${FASTAPI_BASE_URL}/classes/${selectedClassId}/add-student`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ student_email: email }),
      }
    );
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg =
        json?.detail?.message || json?.detail || json?.message || t("Failed");
      Alert.alert(t("Error"), msg);
      return;
    }
    setExistingEmail("");
    await loadRoster(selectedClassId);
  } catch (e) {
    console.error("add-student error", e);
    Alert.alert(t("Error"), String(e.message || e));
  }
};


// BDD 11/9/2025
const handleRemoveFromClass = (email) => {
  if (!selectedClassId) return;

  const doRemove = async () => {
    try {
      console.log(
        "[remove-student] class",
        selectedClassId,
        "email",
        email
      );

      const res = await fetchWithAuth(
        `${FASTAPI_BASE_URL}/classes/${selectedClassId}/remove-student`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ student_email: email }),
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const msg =
          json?.detail?.message ||
          json?.detail ||
          json?.message ||
          t("Failed to remove student");
        console.log("[remove-student] error:", msg);
        Alert.alert(t("Error"), msg);
        return;
      }

      console.log("[remove-student] success:", json);
      await loadRoster(selectedClassId);
    } catch (e) {
      console.error("[remove-student] exception", e);
      Alert.alert(t("Error"), e?.message || String(e));
    }
  };

  // Native: use Alert with buttons
  if (Platform.OS !== "web") {
    Alert.alert(
      t("Remove student"),
      `${t("Remove")} ${email} from this class?`,
      [
        { text: t("Cancel"), style: "cancel" },
        {
          text: t("Remove"),
          style: "destructive",
          onPress: doRemove,
        },
      ],
      { cancelable: true }
    );
    return;
  }

  // Web: use window.confirm so the callback actually runs
  const ok = window.confirm(
    `${t("Remove")} ${email} from this class?`
  );
  if (ok) {
    doRemove();
  }
};

const handleSetSharedPassword = () => {
  if (!selectedClassId) {
    Alert.alert(t("Select class"), t("Please select a class first."));
    return;
  }

  //hshah - FOR PASSWORD GUIDELINES AND INTERACTIVE DIALOG
  setPasswordMode("shared");
  setPasswordTargetEmail(null);
  setShowPasswordDialog(true);

  setTimeout(() => {
    setPasswordValue("");
    setPasswordError("");
    setShowPassword(false);
  }, 0);
};
const handleSetStudentPassword = (email) => {
  if (!selectedClassId) {
    Alert.alert(t("Select class"), t("Please select a class first."));
    return;
  }

  setPasswordMode("student");
  setPasswordTargetEmail(email);
  setShowPasswordDialog(true);

  setTimeout(() => {
    setPasswordValue("");
    setPasswordError("");
    setShowPassword(false);
  }, 0);
};

const submitPassword = async () => {
  if (isSubmittingPassword) return;

  const err = validatePassword(passwordValue, t);
  if (err) {
    setPasswordError(err);
    return;
  }

  setIsSubmittingPassword(true);

  try {
    const url =
      passwordMode === "student"
        ? `${FASTAPI_BASE_URL}/classes/${selectedClassId}/set-student-password`
        : `${FASTAPI_BASE_URL}/classes/${selectedClassId}/set-shared-password`;

    const body =
      passwordMode === "student"
        ? {
            student_email: passwordTargetEmail,
            password: passwordValue,
          }
        : {
            password: passwordValue,
          };

    const res = await fetchWithAuth(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg =
        json?.detail?.message || json?.detail || json?.message || t("Failed");
      Alert.alert(t("Error"), msg);
      return;
    }

    // Capture values before closing dialog (which may trigger cleanup)
    const savedPassword = passwordValue;
    const savedMode = passwordMode;
    const savedClassId = selectedClassId;

    setShowPasswordDialog(false);

    setTimeout(() => {
      showNotification(
        savedMode === "student"
          ? `${t("Password updated for")} ${passwordTargetEmail}.`
          : t("Shared password updated for class."),
        "success"
      );
    }, 100);

    if (savedMode === "shared") {
      setClasses((prev) =>
        prev.map((c) =>
          c.class_id === savedClassId
            ? { ...c, default_password: savedPassword }
            : c
        )
      );
    }
  } catch (e) {
    console.error("set-password error", e);
    Alert.alert(t("Error"), String(e.message || e));
  } finally {
    setIsSubmittingPassword(false);
  }
};

  // Keep selectedClassIdRef in sync so loaders read the latest value
  // without having to list it as a dep.
  useEffect(() => {
    selectedClassIdRef.current = selectedClassId;
  }, [selectedClassId]);

  // ---------- NEW: load roster + classes helpers ----------

  const loadRoster = useCallback(
    async (classId) => {
      const myId = ++rosterLoadIdRef.current;
      const signal = startAbortable(rosterAbortRef);
      if (!classId) {
        setRoster([]);
        setLoadingRoster(false);
        return;
      }
      setLoadingRoster(true);
      try {
        const res = await fetchWithAuth(
          `${FASTAPI_BASE_URL}/classes/${classId}/students`,
          { signal }
        );
        // Stale response — a newer load has started; drop this one.
        if (myId !== rosterLoadIdRef.current) return;
        if (!res.ok) {
          console.log("Failed to fetch roster", res.status);
          setRoster([]);
          return;
        }
        const data = await res.json();
        if (myId !== rosterLoadIdRef.current) return;
        setRoster(Array.isArray(data) ? data : []);
      } catch (e) {
        if (myId !== rosterLoadIdRef.current) return;
        if (isAbortError(e)) return;
        console.log("Error fetching roster", e);
        setRoster([]);
      } finally {
        // Only clear loading state if we're still the active request.
        if (myId === rosterLoadIdRef.current) {
          setLoadingRoster(false);
        }
      }
    },
    [fetchWithAuth]
  );

  // ONE loading state for the class/student list (no per-request flicker).
  const listLoading = loadingClasses || loadingRoster;

  const selectedClass =
    classes.find((c) => c.class_id === selectedClassId) || null;
  const userRole = (user?.role || "").toLowerCase();
  const canCreateClass = ["teacher", "superadmin", "schooladmin", "partneradmin"].includes(userRole);
  const canManageSelectedClass =
    userRole === "superadmin" ||
    (selectedClass && String(selectedClass.role || "").toLowerCase() === "teacher");
  const canRenameSelectedClass =
    canManageSelectedClass &&
    selectedClass &&
    classRenameValue.trim() &&
    classRenameValue.trim() !== (selectedClass.class_name || "").trim();

  const loadClassCollections = useCallback(
    async (classId) => {
      const myId = ++collectionsLoadIdRef.current;
      const signal = startAbortable(collectionsAbortRef);
      if (!classId) {
        setClassCollections([]);
        setCollectionError("");
        return;
      }
      setLoadingCollections(true);
      setCollectionError("");
      try {
        const board = String(contentDb || user?.content_db || "anatomy").toLowerCase();
        // Fire both fetches in parallel — they're independent.
        const [allRes, assignedRes] = await Promise.all([
          fetchWithAuth(
            `${FASTAPI_BASE_URL}/collections?board=${encodeURIComponent(board)}`,
            { signal }
          ),
          fetchWithAuth(`${FASTAPI_BASE_URL}/classes/${classId}/collections`, { signal }),
        ]);
        if (myId !== collectionsLoadIdRef.current) return;
        if (!allRes.ok) {
          throw new Error(`Failed to load collections (${allRes.status})`);
        }
        if (!assignedRes.ok) {
          throw new Error(`Failed to load class collections (${assignedRes.status})`);
        }
        const [allJson, assignedJson] = await Promise.all([
          allRes.json().catch(() => []),
          assignedRes.json().catch(() => []),
        ]);
        if (myId !== collectionsLoadIdRef.current) return;
        const activeIds = new Set(
          Array.isArray(assignedJson)
            ? assignedJson.map((c) => Number(c.collection_id))
            : []
        );

        const merged = (Array.isArray(allJson) ? allJson : [])
          .map((c) => ({
            id: Number(c.id),
            name: c.name || `Collection ${c.id}`,
            base_board: c.base_board,
            is_active: activeIds.has(Number(c.id)),
          }))
          .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        setClassCollections(merged);
      } catch (e) {
        if (myId !== collectionsLoadIdRef.current) return;
        if (isAbortError(e)) return;
        console.error("loadClassCollections error", e);
        setClassCollections([]);
        setCollectionError(e?.message || String(e));
      } finally {
        if (myId === collectionsLoadIdRef.current) {
          setLoadingCollections(false);
        }
      }
    },
    [contentDb, fetchWithAuth, user?.content_db]
  );

  const loadClassesAndRoster = useCallback(async (preferredClassId = null) => {
    const myId = ++classesLoadIdRef.current;
    // New school/class load: cancel everything still in flight for the old one.
    abortInFlightLoads();
    const signal = startAbortable(classesAbortRef);
    setLoadingClasses(true);
    try {
      let data;

      // If user can view all classes and a school is selected, use the observer endpoint
      if (canViewAllClasses && loadSchoolId) {
        const params = new URLSearchParams({
          observer_all: "1",
          school_id: String(loadSchoolId),
        });
        const res = await fetchWithAuth(
          `${FASTAPI_BASE_URL}/classes/teacher/classes?${params}`,
          { signal }
        );
        // Stale — user has moved on to another school; abort silently.
        if (myId !== classesLoadIdRef.current) return;
        if (!res.ok) {
          console.log("Failed to fetch classes (observer)", res.status);
          setClasses([]);
          setSelectedClassId(null);
          setRoster([]);
          return;
        }
        const json = await res.json();
        if (myId !== classesLoadIdRef.current) return;
        // /teacher/classes returns { classes: [{id, name, school_name}] }
        // Normalize to match /classes/mine shape
        data = (json.classes || []).map((c) => ({
          class_id: c.id,
          class_name: c.name,
          school_name: c.school_name,
          default_password: c.default_password || null,
        }));
      } else {
        const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/classes/mine`, { signal });
        if (myId !== classesLoadIdRef.current) return;
        if (!res.ok) {
          console.log("Failed to fetch classes", res.status);
          setClasses([]);
          setSelectedClassId(null);
          setRoster([]);
          return;
        }
        data = await res.json();
        if (myId !== classesLoadIdRef.current) return;
      }

      if (Array.isArray(data) && data.length > 0) {
        setClasses(data);
        // Read selectedClassId via ref so this callback doesn't need to
        // re-memoize on every class switch.
        const currentClassId = selectedClassIdRef.current;
        const firstId =
          preferredClassId &&
          data.some((c) => c.class_id === preferredClassId)
            ? preferredClassId
            : currentClassId &&
              data.some((c) => c.class_id === currentClassId)
            ? currentClassId
            : data[0].class_id;
        setSelectedClassId(firstId);
        // Fire roster + collections in parallel — each loader has its own
        // staleness check, so we don't need to await them here.
        await Promise.all([loadRoster(firstId), loadClassCollections(firstId)]);
      } else {
        setClasses([]);
        setSelectedClassId(null);
        setRoster([]);
        setClassCollections([]);
      }
    } catch (e) {
      if (myId !== classesLoadIdRef.current) return;
      if (isAbortError(e)) return;
      console.log("Error fetching classes", e);
      setClasses([]);
      setSelectedClassId(null);
      setRoster([]);
      setClassCollections([]);
    } finally {
      // Only the newest load clears the shared list-loading flag.
      if (myId === classesLoadIdRef.current) setLoadingClasses(false);
    }
  }, [fetchWithAuth, loadRoster, loadClassCollections, canViewAllClasses, loadSchoolId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (allowed) {
      loadClassesAndRoster();
    }
  }, [allowed, loadClassesAndRoster, schoolLoadTick]);

  const handleSchoolChange = (id, name) => {
    setSelectedSchoolId(id);
    setSelectedSchoolName(name);
    setSelectedClassId(null);
    setRoster([]);
    setClassCollections([]);
    setLoadingClasses(true); // one loading state from the moment of the switch
    // Invalidate anything still in flight for the previous school right away,
    // so a late response can't apply during the debounce window.
    abortInFlightLoads();
    classesLoadIdRef.current += 1;
    rosterLoadIdRef.current += 1;
    collectionsLoadIdRef.current += 1;
    if (schoolDebounceRef.current) clearTimeout(schoolDebounceRef.current);
    schoolDebounceRef.current = setTimeout(() => {
      schoolDebounceRef.current = null;
      setLoadSchoolId(id);
      setSchoolLoadTick((n) => n + 1);
    }, SCHOOL_SWITCH_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (selectedClass?.class_name) {
      setClassRenameValue(selectedClass.class_name);
    }
  }, [selectedClass?.class_name]);

  const handleSelectClass = async (classId) => {
    setSelectedClassId(classId);
    await loadRoster(classId);
    await loadClassCollections(classId);
  };

  const handleCreateClass = async () => {
    const name = (newClassName || "").trim();
    if (!name) {
      setClassError(t("Enter a class name."));
      return;
    }
    setClassSaving(true);
    setClassError("");
    try {
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/classes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          ...(canViewAllClasses && selectedSchoolId
            ? { school_id: Number(selectedSchoolId) }
            : {}),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          json?.detail?.message || json?.detail || json?.message || t("Failed to create class");
        setClassError(msg);
        Alert.alert(t("Error"), msg);
        return;
      }
      setNewClassName("");
      const newId = json.class_id || null;
      setSelectedClassId(newId);
      await loadClassesAndRoster(newId);
      showNotification(t("Class created."), "success");
    } catch (e) {
      console.error("create class error", e);
      const msg = e?.message || String(e);
      setClassError(msg);
      Alert.alert(t("Error"), msg);
    } finally {
      setClassSaving(false);
    }
  };

  const handleRenameClass = async () => {
    if (!selectedClassId) return;
    const name = (classRenameValue || "").trim();
    if (!name) {
      setClassError(t("Enter a new class name."));
      return;
    }
    setClassRenameSaving(true);
    try {
      const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/classes/${selectedClassId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          json?.detail?.message || json?.detail || json?.message || t("Failed to rename class");
        Alert.alert(t("Error"), msg);
        return;
      }
      setClassRenameValue("");
      await loadClassesAndRoster();
      showNotification(t("Class renamed."), "success");
    } catch (e) {
      console.error("rename class error", e);
      Alert.alert(t("Error"), e?.message || String(e));
    } finally {
      setClassRenameSaving(false);
    }
  };

  const handleToggleCollection = async (collectionId, nextActive) => {
    if (!selectedClassId) {
      Alert.alert(t("Select class"), t("Please select a class first."));
      return;
    }
    setUpdatingCollectionId(collectionId);
    try {
      const res = await fetchWithAuth(
        `${FASTAPI_BASE_URL}/classes/${selectedClassId}/collections`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ collection_id: collectionId, is_active: nextActive }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        const msg =
          json?.detail?.message || json?.detail || json?.message || t("Failed to update collection");
        Alert.alert(t("Error"), msg);
        return;
      }
      setClassCollections((prev) =>
        prev.map((c) =>
          c.id === collectionId ? { ...c, is_active: nextActive } : c
        )
      );
    } catch (e) {
      console.error("toggle collection error", e);
      Alert.alert(t("Error"), e?.message || String(e));
    } finally {
      setUpdatingCollectionId(null);
    }
  };

  // ---------- submit (existing) + reload roster on success ----------

  const handleSubmit = async () => {
    if (!allowed) {
      Alert.alert(t("Not allowed"), t("You are not allowed to create students."));
      return;
    }

    setBackendMessage("");
    setCreatedSummaries([]);

    // AIT-267: validate every started row before building the payload so a
    // missing grade is reported inline instead of the row being dropped.
    const nothingMsg = `${t("Nothing to submit")}. ${t(
      "Please fill at least one student row with name, grade, and phone."
    )}`;
    if (!hasStartedRow(rows)) {
      setRowErrors({});
      showNotification(nothingMsg, "error");
      return;
    }
    const validation = validateStudentRows(rows);
    if (Object.keys(validation).length > 0) {
      setRowErrors(validation);
      return;
    }
    setRowErrors({});

    const users = buildUsersPayload();
    if (!users.length) {
      showNotification(nothingMsg, "error");
      return;
    }

    // Collect grades for the envelope; Django /inst/ expects grade strings like "09"
    const grades = Array.from(
      new Set(
        users
          .map((u) =>
            u.grade_no != null
              ? String(u.grade_no).padStart(2, "0")
              : null
          )
          .filter(Boolean)
      )
    );

    const payload = {
      inst_name: "ignored", // FastAPI overrides to teacher’s institute
      board: "cbse",
      language: "english",
      grades,
      device_limit: 100,
      subscription_years: 1,
      extend_subscription: false,
      streaming_default: true,
      users,
      selected_class_id: selectedClassId || null,  // BDD 11/13/2025
    };

    setIsSaving(true);

    try {
      const res = await fetchWithAuth(
        `${FASTAPI_BASE_URL}/signup/bulk-institute-users-self`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        const detail =
          json?.detail?.message ||
          json?.detail ||
          json?.message ||
          t("Bulk create failed");
        setBackendMessage(detail);
        Alert.alert(t("Error"), detail);
        return;
      }

      // BDD 11/13/2025
      // --- success path ---
      const msg = json.message || "Done";
      let extra = ""; // <- ensure it's always defined

      if (Array.isArray(json.data) && json.data.length) {
        // If backend returned structured rows (objects with name/login_id/login_email)
        if (typeof json.data[0] === "object") {
          const summaries = json.data.map((r) => ({
            name: r.name || "(No name)",
            loginId: r.login_id || "",
            loginEmail: r.login_email || "",
            password: DEFAULT_PASSWORD,
          }));
          setCreatedSummaries(summaries);
          setBackendMessage(msg);
        } else {
          // If backend returned a string array (notes)
          extra = json.data.join("\n");
          setBackendMessage(extra || msg);
        }
      } else {
        // Fallback: show preview built from inputs
        setBackendMessage(msg);
        if (instCode) {
          const summaries = users.map((u, idx) => {
            const rollNorm = normalizeRoll(u.roll_number);
            const suffix = rollNorm || `s${String(idx + 1).padStart(3, "0")}`;
            const loginEmail = `${instCode}.${suffix}@student.noemail.saras3d`;
            const name = `${u.fname || ""} ${u.lname || ""}`.trim() || "(No name)";
            return { name, loginId: "", loginEmail, password: DEFAULT_PASSWORD };
          });
          setCreatedSummaries(summaries);
        }
      }

      // This now compiles because `extra` is always defined
      if (!extra) {
        setRows([emptyRow()]);
        Alert.alert(t("Success"), t("Student accounts created."));
      } else {
        Alert.alert(t("Completed with notes"), extra);
      }

      // Refresh classes + roster so new students appear immediately.
      // Fire-and-forget on purpose: the button's saving state must NOT be
      // coupled to this secondary refresh completing. The create already
      // succeeded (200) above; the loaders are staleness-guarded and own
      // their own loading flags. Awaiting here previously left the spinner
      // stuck forever if any refresh fetch stalled (fetchWithAuth has no
      // timeout). Mirrors InternalAdminPanel.createStudent's fire-and-forget.
      loadClassesAndRoster().catch((e) =>
        console.error("post-create roster refresh failed", e)
      );
    } catch (err) {
      console.error("bulk-institute-users-self error", err);
      const m = err?.message || String(err);
      setBackendMessage(m);
      Alert.alert(t("Error"), m);
    } finally {
      setIsSaving(false);
    }
  };

  if (!allowed) {
    return (
      <View style={styles.container}>
        <Text className={styles.title}>{t("Manage Students")}</Text>
        <Text style={styles.dim}>
          {t("Your account does not have permission to create institute users.")}
        </Text>
      </View>
    );
  }

  return (
    <>
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.scrollContent}
    >
      {/* Constrained width wrapper */}
      <View style={styles.contentWrapper}>
        {/* Header */}
        <Text style={styles.title}>{t("Manage Students")}</Text>
        <Text style={styles.subtitle}>
          {canBulkCreate
            ? t("Add new students for your institute. We'll generate login IDs automatically.")
            : t("Manage the students in your classes.")}
        </Text>

        {/* School selector (for users who can view all classes) */}
        {canViewAllClasses && schools.length > 1 && Platform.OS === "web" && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t("Select School")}</Text>
            <select
              value={selectedSchoolId ?? ""}
              onChange={(e) => {
                const id = e.target.value ? Number(e.target.value) : null;
                const match = schools.find((s) => s.id === id);
                handleSchoolChange(id, match?.name || null);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${dk ? "rgba(148,163,184,0.25)" : "#d1d5db"}`,
                background: dk ? "rgba(30,41,59,0.8)" : "#fff",
                color: dk ? "#f1f5f9" : "#1e293b",
                fontSize: 14,
                outline: "none",
              }}
            >
              <option value="">{t("-- Select a school --")}</option>
              {schools.map((s) => (
                <option key={s.id || s.code || s.name} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </View>
        )}

        {/* Add Class (always above pills if allowed) */}
        {canCreateClass && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t("Create Class")}</Text>
            <Text style={styles.sectionSubtitle}>
              {t("Create a new class")}
            </Text>
            <View style={styles.inlineRow}>
              <TextInput
                style={[styles.input, styles.inlineInput]}
                placeholder={t("New class name")}
                placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                value={newClassName}
                onChangeText={setNewClassName}
              />
              <TouchableOpacity
                onPress={handleCreateClass}
                disabled={classSaving}
                style={[styles.primaryBtn, classSaving && styles.btnDisabled]}
              >
                <Text style={styles.primaryBtnText}>
                  {classSaving ? t("Creating...") : t("Create class")}
                </Text>
              </TouchableOpacity>
            </View>
            {!!classError && <Text style={styles.errorText}>{classError}</Text>}
          </View>
        )}

        {/* Class selector pills */}
        {classes.length > 0 && (
          <>
            <View style={styles.sectionTitleRow}>
              <Text style={styles.sectionTitle}>{t("Classes")}</Text>
            </View>
            <View style={styles.classPillsRow}>
              {classes.map((cls) => (
                <TouchableOpacity
                  key={cls.class_id}
                  onPress={() => handleSelectClass(cls.class_id)}
                  style={[
                    styles.classPill,
                    cls.class_id === selectedClassId && styles.classPillActive,
                  ]}
                >
                  <Text
                    style={[
                      styles.classPillText,
                      cls.class_id === selectedClassId && styles.classPillTextActive,
                    ]}
                  >
                    {cls.class_name}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </>
        )}

        {/* Manage selected class (only if user can manage this class) */}
        {canManageSelectedClass && (
          <>
            <View style={styles.sectionCard}>
              <Text style={styles.sectionTitle}>{t("Manage Selected Class")}</Text>
              <View style={styles.inlineRow}>
                <Text style={styles.dim}>
                  {t("Selected class:")} {selectedClass?.class_name}
                </Text>
              </View>
              <View style={[styles.inlineRow, styles.inlineRowSpaced]}>
                <TextInput
                  style={[styles.input, styles.inlineInput]}
                  placeholder={t("Rename selected class")}
                  placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                  value={classRenameValue}
                  onChangeText={setClassRenameValue}
                />
                <TouchableOpacity
                  onPress={handleRenameClass}
                  disabled={!canRenameSelectedClass || classRenameSaving}
                  style={[
                    styles.secondaryBtn,
                    (!canRenameSelectedClass || classRenameSaving) && styles.btnDisabled,
                  ]}
                >
                  <Text style={styles.secondaryBtnText}>
                    {classRenameSaving ? t("Saving...") : t("Rename")}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>

            <View style={styles.sectionCard}>
              <Text style={styles.sectionTitle}>{t("Assign Collections to the Class")}</Text>
              <Text style={styles.sectionSubtitle}>
                {t("Collections are tied to your curriculum board.")}{" "}
                {t("Select which collections this class can access.")}
              </Text>
              {!!collectionError && <Text style={styles.errorText}>{collectionError}</Text>}
              {!selectedClassId ? (
                <Text style={styles.dim}>{t("Select a class to manage collections.")}</Text>
              ) : loadingCollections ? (
                <View style={styles.loadingRow}>
                  <ActivityIndicator />
                  <Text style={styles.dim}>{t("Loading collections...")}</Text>
                </View>
              ) : classCollections.length === 0 ? (
                <Text style={styles.dim}>{t("No collections found for this board.")}</Text>
              ) : (
                <View style={styles.collectionList}>
                  {classCollections.map((c) => (
                    <TouchableOpacity
                      key={c.id}
                      style={styles.collectionRow}
                      onPress={() => handleToggleCollection(c.id, !c.is_active)}
                      disabled={updatingCollectionId === c.id}
                    >
                      <MaterialCommunityIcons
                        name={c.is_active ? "checkbox-marked" : "checkbox-blank-outline"}
                        size={18}
                        color={c.is_active ? "#16a34a" : dk ? "#94a3b8" : "#64748b"}
                        style={styles.collectionIcon}
                      />
                      <Text style={styles.collectionName}>{c.name}</Text>
                      {updatingCollectionId === c.id && (
                        <ActivityIndicator size="small" style={styles.collectionSpinner} />
                      )}
                    </TouchableOpacity>
                  ))}
                </View>
              )}
            </View>

          </>
        )}

        {selectedClass && !canBulkCreate && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>{t("Adding new students")}</Text>
            <Text style={[styles.sectionSubtitle, styles.sectionSubtitleTight]}>
              {t("Your role can't create new student accounts. You can add existing students to this class, remove students, or reset passwords below.")}
            </Text>
          </View>
        )}

        {selectedClass && canBulkCreate && (
          <View style={styles.sectionCard}>
            <Text style={styles.sectionTitle}>
              {t("Manage Students Within the Class")}
            </Text>
            <Text style={[styles.sectionSubtitle, styles.sectionSubtitleTight]}>
              {t("Add, remove, or edit students in the selected class.")}
            </Text>

            {/* Student Form Cards */}
            {rows.map((row, idx) => (
              <View key={idx} style={styles.formCard}>
                <View style={styles.formCardHeader}>
                  <Text style={styles.formCardTitle}>{t("STUDENT")} #{idx + 1}</Text>
                  {rows.length > 1 && (
                    <TouchableOpacity onPress={() => removeRow(idx)}>
                      <Text style={styles.removeLink}>{t("Remove")}</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <View style={styles.formCardDivider} />

                {/* Two-column grid */}
                <View style={styles.formGrid}>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("First name")}</Text>
                    <TextInput
                      style={styles.input}
                      placeholder={t("Enter first name")}
                      placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                      value={row.fname}
                      onChangeText={(v) => updateRow(idx, { fname: v })}
                    />
                  </View>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("Last name")}</Text>
                    <TextInput
                      style={styles.input}
                      placeholder={t("Enter last name")}
                      placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                      value={row.lname}
                      onChangeText={(v) => updateRow(idx, { lname: v })}
                    />
                  </View>
                </View>

                <View style={styles.formGrid}>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("Grade")} <Text style={styles.fieldHint}>(e.g. 9)</Text></Text>
                    <TextInput
                      style={styles.input}
                      placeholder={t("Enter grade")}
                      placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                      keyboardType="numeric"
                      value={String(row.grade_no || "")}
                      onChangeText={(v) => updateRow(idx, { grade_no: v })}
                    />
                    {!!rowErrors[idx]?.grade && (
                      <Text style={styles.errorText}>{t(rowErrors[idx].grade)}</Text>
                    )}
                  </View>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("Roll #")} <Text style={styles.fieldHint}>({t("optional")})</Text></Text>
                    <TextInput
                      style={styles.input}
                      placeholder={t("Enter roll number")}
                      placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                      value={row.roll_number}
                      onChangeText={(v) => updateRow(idx, { roll_number: v })}
                    />
                  </View>
                </View>

                <View style={styles.formGrid}>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("Phone")} <Text style={styles.fieldHint}>({t("optional")})</Text></Text>
                    <View style={styles.phoneRow}>
                      <TextInput
                        style={[styles.input, styles.countryCodeInput]}
                        placeholder="+1"
                        placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                        value={row.country_code}
                        onChangeText={(v) => updateRow(idx, { country_code: v })}
                      />
                      <TextInput
                        style={[styles.input, styles.phoneInput]}
                        placeholder={t("Phone number")}
                        placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                        keyboardType="phone-pad"
                        value={row.contact_number}
                        onChangeText={(v) => updateRow(idx, { contact_number: v })}
                      />
                    </View>
                  </View>
                  <View style={styles.formField}>
                    <Text style={styles.fieldLabel}>{t("Gender")} <Text style={styles.fieldHint}>(M/F, {t("optional")})</Text></Text>
                    <View style={styles.selectWrapper}>
                      {Platform.OS === "web" ? (
                        <select
                          value={row.gender}
                          onChange={(e) => updateRow(idx, { gender: e.target.value })}
                          style={styles.selectNative}
                        >
                          <option value="">{t("Select gender")}</option>
                          <option value="m">{t("Male")}</option>
                          <option value="f">{t("Female")}</option>
                        </select>
                      ) : (
                        <TextInput
                          style={styles.input}
                          placeholder={t("M or F")}
                          placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                          value={row.gender}
                          onChangeText={(v) => updateRow(idx, { gender: v })}
                        />
                      )}
                    </View>
                  </View>
                </View>

                {/* Show Add another student on the last card only */}
                {idx === rows.length - 1 && (
                  <TouchableOpacity onPress={addRow} style={styles.addAnotherBtn}>
                    <Text style={styles.addAnotherBtnText}>{t("+ Add another student")}</Text>
                  </TouchableOpacity>
                )}
              </View>
            ))}

            {/* Create Accounts button below all cards */}
            <TouchableOpacity
              onPress={handleSubmit}
              style={[styles.createAccountsBtn, isSaving && { opacity: 0.6 }]}
              disabled={isSaving}
            >
              {isSaving ? (
                <ActivityIndicator color="#ffffff" />
              ) : (
                <Text style={styles.createAccountsBtnText}>{t("Create Accounts")}</Text>
              )}
            </TouchableOpacity>

            {!!backendMessage && (
              <Text style={styles.backendMsg}>{backendMessage}</Text>
            )}

            {createdSummaries.length > 0 && (
              <View style={styles.summaryCard}>
                <Text style={styles.cardTitle}>{t("Created Accounts")}</Text>
                {createdSummaries.map((s, idx) => (
                  <View key={idx} style={styles.studentListItem}>
                    <Text style={styles.studentName}>{s.name}</Text>
                    <Text style={styles.studentMeta}>
                      <Text style={styles.metaLabel}>{t("LOGIN ID:")}</Text> {s.loginId || (s.loginEmail ? s.loginEmail.split("@")[0] : "")}
                    </Text>
                    <Text style={styles.studentMeta}>
                      <Text style={styles.metaLabel}>{t("LOGIN EMAIL:")}</Text> {s.loginEmail || ""}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        )}

        {/* Roster for selected class */}
        {selectedClass && (
          <View style={styles.rosterCard}>
            <View style={styles.rosterHeader}>
              <Text style={styles.cardTitle}>
                {t("Students in")} {selectedClass.class_name}
              </Text>
              <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                <View style={styles.sharedPasswordBadge}>
                  <Text style={styles.sharedPasswordLabel}>{t("SHARED PASSWORD:")}</Text>
                  <Text style={styles.sharedPasswordValue}>
                    {selectedClass.default_password || t("Not set")}
                  </Text>
                </View>
                {canManageSelectedClass && (
                  <TouchableOpacity
                    onPress={handleSetSharedPassword}
                    style={{
                      paddingHorizontal: 10,
                      paddingVertical: 5,
                      borderRadius: 6,
                      backgroundColor: dk ? "#334155" : "#e2e8f0",
                    }}
                  >
                    <Text style={{ fontSize: 12, fontWeight: "600", color: dk ? "#94a3b8" : "#475569" }}>
                      {selectedClass.default_password ? t("Change") : t("Set")}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>

            {/* Search roster / Add by email */}
            <View style={styles.searchRow}>
              <TextInput
                style={styles.searchInput}
                placeholder={t("Search by name, login ID, or email")}
                placeholderTextColor={dk ? "#64748b" : "#9ca3af"}
                value={existingEmail}
                onChangeText={setExistingEmail}
              />
            </View>

            {listLoading && (
              <ActivityIndicator style={{ marginVertical: 16 }} color={dk ? "#60a5fa" : "#0077b6"} />
            )}

            {!listLoading && roster.length === 0 && (
              <Text style={styles.emptyText}>
                {t("No students linked to this class yet.")}
              </Text>
            )}

            {!listLoading && roster.length > 0 && filteredRoster.length === 0 && (
              <Text style={styles.emptyText}>
                {t("No students match your search.")}
              </Text>
            )}

            {!listLoading && filteredRoster.map((s) => (
              <View key={s.email} style={styles.studentCard}>
                <View style={styles.studentCardLeft}>
                  <Text style={styles.studentCardName}>{s.name}</Text>
                  <Text style={styles.studentCardMeta}>
                    <Text style={styles.metaLabel}>{t("LOGIN ID")}</Text>
                  </Text>
                  <Text style={styles.studentCardEmail}>{(s.loginId ?? s.login_id ?? "").trim()}</Text>
                  <Text style={styles.studentCardMeta}>
                    <Text style={styles.metaLabel}>{t("LOGIN EMAIL:")}</Text> <Text style={styles.emailLink}>{s.email}</Text>
                  </Text>
                </View>
                <View style={styles.studentCardRight}>
                  <Text style={styles.statusLabel}>{t("STATUS:")}</Text>
                  <Text style={styles.statusValue}>{s.status}</Text>
                </View>
                <View style={styles.studentCardActions}>
                  <TouchableOpacity
                    onPress={() => handleSetStudentPassword(s.email)}
                    style={styles.actionBtn}
                  >
                    <Text style={styles.actionBtnText}>{t("Set password")}</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    onPress={() => handleRemoveFromClass(s.email)}
                    style={styles.actionBtnDanger}
                  >
                    <Text style={styles.actionBtnTextDanger}>{t("Remove")}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ))}

            {/* Add button at bottom */}
            <TouchableOpacity
              onPress={handleAddExistingToClass}
              style={styles.addExistingBtn}
            >
              <Text style={styles.addExistingBtnText}>{t("Add")}</Text>
            </TouchableOpacity>
          </View>
        )}
      </View>
    </ScrollView>
    {showPasswordDialog && (
      <View style={styles.modalOverlay}>
        <View style={styles.modalCard}>
          {/* <Text style={styles.modalTitle}>
            {t("Set Student Password")}
          </Text> */}
          <Text style={styles.modalTitle}>
            {passwordMode === "student"
              ? t("Set Student Password")
              : t("Set Shared Class Password")}
          </Text>

          {/* <Text style={styles.modalSubtitle}>
            {passwordTargetEmail}
          </Text> */}
          {passwordMode === "student" && (
          <Text style={styles.modalSubtitle}>
            {passwordTargetEmail}
          </Text>
        )}

          <View style={styles.passwordInputWrapper}>
            <TextInput
              autoFocus
              secureTextEntry={!showPassword}
              placeholder={t("Enter password")}
              placeholderTextColor={dk ? "#64748b" : "#6b7280"}
              value={passwordValue}
              onChangeText={(v) => {
                setPasswordValue(v);
                setPasswordError(validatePassword(v, t));
              }}
              style={[
                styles.input,
                styles.passwordInput,
                passwordError && styles.inputError,
              ]}
            />

            <TouchableOpacity
              onPress={() => setShowPassword((v) => !v)}
              style={styles.eyeButton}
            >
              <MaterialIcons
                name={showPassword ? "visibility-off" : "visibility"}
                size={22}
                color={dk ? "#64748b" : "#9ca3af"}
              />
            </TouchableOpacity>
          </View>


          {!!passwordError && (
            <Text style={styles.errorText}>{passwordError}</Text>
          )}

          <View style={styles.rowInline}>
            <TouchableOpacity
              onPress={() => setShowPasswordDialog(false)}
              disabled={isSubmittingPassword}
              style={[
                styles.smallPillButtonDanger,
                isSubmittingPassword && { opacity: 0.5 },
              ]}
            >
              <Text style={styles.smallPillButtonText}>{t("Cancel")}</Text>
            </TouchableOpacity>


            <TouchableOpacity
              onPress={submitPassword}
              disabled={
                !!passwordError ||
                !passwordValue ||
                isSubmittingPassword
              }
              style={[
                styles.smallPillButton,
                (passwordError || !passwordValue || isSubmittingPassword) && {
                  opacity: 0.5,
                },
              ]}
            >
              {isSubmittingPassword ? (
                <ActivityIndicator size="small" color={dk ? "#f1f5f9" : "#020817"} />
              ) : (
                <Text style={styles.smallPillButtonText}>{t("Submit")}</Text>
              )}
            </TouchableOpacity>

          </View>
        </View>
      </View>
    )}
    </>
  );
}

const getStyles = (dk) => StyleSheet.create({
  // ═══════════════════════════════════════════════════════════════════════════
  // MANAGE STUDENTS - Modern card-based design with constrained width
  // Professional blue (#0077b6) primary, responsive layout
  // Dark mode support via dk flag
  // ═══════════════════════════════════════════════════════════════════════════

  container: {
    flex: 1,
    backgroundColor: dk ? "#0f172a" : "#f8fafc",
    width: "100%",
  },
  scrollContent: {
    paddingVertical: 24,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  contentWrapper: {
    width: "100%",
    maxWidth: 900,
  },

  // ─── Header Section ────────────────────────────────────────────────────────
  title: {
    fontSize: 28,
    fontWeight: "700",
    marginBottom: 8,
    color: dk ? "#f1f5f9" : "#0f172a",
  },
  subtitle: {
    fontSize: 15,
    color: dk ? "#94a3b8" : "#64748b",
    marginBottom: 24,
    lineHeight: 22,
  },

  // ─── Class Selector Pills ──────────────────────────────────────────────────
  classPillsRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 24,
  },
  classPill: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 24,
    borderWidth: 2,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  classPillActive: {
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    borderColor: dk ? "#60a5fa" : "#0077b6",
    shadowColor: dk ? "#60a5fa" : "#0077b6",
    shadowOpacity: 0.2,
  },
  classPillText: {
    fontSize: 14,
    fontWeight: "600",
    color: dk ? "#94a3b8" : "#475569",
  },
  classPillTextActive: {
    color: "#ffffff",
  },
  sectionTitleRow: {
    paddingLeft: 16,
    paddingRight: 16,
    marginBottom: 6,
  },

  sectionCard: {
    backgroundColor: dk ? "#0f172a" : "#ffffff",
    borderWidth: 1,
    borderColor: dk ? "#1f2937" : "#e2e8f0",
    borderRadius: 14,
    padding: 16,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: dk ? "#f8fafc" : "#0f172a",
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 12,
    color: dk ? "#94a3b8" : "#64748b",
    marginBottom: 12,
  },
  sectionSubtitleTight: {
    marginBottom: 8,
  },
  inlineRow: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
  },
  inlineRowSpaced: {
    marginTop: 10,
  },
  inlineInput: {
    flex: 1,
  },
  primaryBtn: {
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 10,
  },
  primaryBtnText: {
    color: "#ffffff",
    fontWeight: "700",
    fontSize: 13,
  },
  secondaryBtn: {
    borderWidth: 1,
    borderColor: dk ? "#334155" : "#cbd5f5",
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: dk ? "#0b1220" : "#f8fafc",
  },
  secondaryBtnText: {
    color: dk ? "#e2e8f0" : "#0f172a",
    fontSize: 12,
    fontWeight: "600",
  },
  btnDisabled: {
    opacity: 0.6,
  },
  collectionList: {
    gap: 6,
  },
  collectionRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: dk ? "#1f2937" : "#eef2f7",
  },
  collectionIcon: {
    marginRight: 8,
  },
  collectionName: {
    flex: 1,
    fontSize: 13,
    color: dk ? "#e2e8f0" : "#111827",
  },
  collectionSpinner: {
    marginLeft: 8,
  },
  loadingRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  // ─── Student Form Cards (Compact) ─────────────────────────────────────────
  formCard: {
    borderRadius: 10,
    padding: 14,
    marginBottom: 12,
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 2,
  },
  formCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  formCardTitle: {
    fontSize: 11,
    fontWeight: "700",
    color: dk ? "#60a5fa" : "#0077b6",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  formCardDivider: {
    height: 1,
    backgroundColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    marginBottom: 12,
  },
  addAnotherBtn: {
    marginTop: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: dk ? "#60a5fa" : "#0077b6",
    backgroundColor: "transparent",
    alignSelf: "flex-start",
  },
  addAnotherBtnText: {
    fontSize: 13,
    fontWeight: "600",
    color: dk ? "#60a5fa" : "#0077b6",
  },
  createAccountsBtn: {
    marginTop: 16,
    paddingVertical: 14,
    paddingHorizontal: 28,
    borderRadius: 8,
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    alignItems: "center",
    shadowColor: dk ? "#60a5fa" : "#0077b6",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 3,
  },
  createAccountsBtnText: {
    fontSize: 15,
    fontWeight: "700",
    color: "#ffffff",
  },
  removeLink: {
    fontSize: 12,
    fontWeight: "600",
    color: "#ef4444",
  },

  // ─── Form Grid (Two Columns, Compact) ─────────────────────────────────────
  formGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  formField: {
    flex: 1,
    minWidth: 180,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: "600",
    color: dk ? "#94a3b8" : "#374151",
    marginBottom: 4,
  },
  fieldHint: {
    fontSize: 11,
    fontWeight: "400",
    color: dk ? "#64748b" : "#9ca3af",
  },

  // ─── Inputs (Compact) ─────────────────────────────────────────────────────
  input: {
    width: "100%",
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#d1d5db",
    color: dk ? "#f1f5f9" : "#1e293b",
    backgroundColor: dk ? "#0f172a" : "#f9fafb",
    fontSize: 13,
  },
  inputFocused: {
    borderColor: dk ? "#60a5fa" : "#0077b6",
    backgroundColor: dk ? "#1e293b" : "#ffffff",
  },
  phoneRow: {
    flexDirection: "row",
    gap: 8,
  },
  countryCodeInput: {
    flex: 0,
    width: 70,
    textAlign: "center",
  },
  phoneInput: {
    flex: 1,
  },
  selectWrapper: {
    width: "100%",
    position: "relative",
  },
  selectNative: {
    width: "100%",
    height: 38,
    paddingLeft: 10,
    paddingRight: 28,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#d1d5db",
    borderStyle: "solid",
    color: dk ? "#f1f5f9" : "#1e293b",
    backgroundColor: dk ? "#0f172a" : "#f9fafb",
    fontSize: 13,
    cursor: "pointer",
    outline: "none",
    backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E\")",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 10px center",
    WebkitAppearance: "none",
    MozAppearance: "none",
    appearance: "none",
  },

  // ─── Add/Remove Links ─────────────────────────────────────────────────────
  addLink: {
    marginBottom: 20,
    paddingVertical: 8,
    alignSelf: "flex-start",
  },
  addLinkText: {
    fontSize: 14,
    fontWeight: "600",
    color: dk ? "#60a5fa" : "#0077b6",
  },

  rowInline: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    marginBottom: 10,
  },

  // ─── Action Buttons ────────────────────────────────────────────────────────
  addBtn: {
    marginTop: 4,
    marginBottom: 16,
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    backgroundColor: dk ? "rgba(96,165,250,0.12)" : "rgba(0, 119, 182, 0.08)",
  },
  addText: {
    color: dk ? "#60a5fa" : "#0077b6",
    fontSize: 14,
    fontWeight: "600",
  },
  submitBtn: {
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: "center",
    marginBottom: 10,
    shadowColor: dk ? "#60a5fa" : "#0077b6",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 3,
  },
  submitText: {
    color: "#ffffff",
    fontWeight: "700",
    fontSize: 15,
  },
  removeBtn: {
    marginTop: 8,
    alignSelf: "flex-end",
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 4,
  },
  removeText: {
    color: "#ef4444",
    fontSize: 12,
    fontWeight: "600",
  },
  backendMsg: {
    marginTop: 8,
    fontSize: 13,
    color: dk ? "#94a3b8" : "#64748b",
    textAlign: "center",
  },
  dim: {
    color: dk ? "#64748b" : "#94a3b8",
    marginTop: 4,
    fontSize: 12,
  },

  // ─── Summary Card (Created Accounts) ───────────────────────────────────────
  summaryCard: {
    marginTop: 24,
    padding: 20,
    borderRadius: 12,
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    borderLeftWidth: 4,
    borderLeftColor: "#10b981",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "700",
    marginBottom: 16,
    color: dk ? "#f1f5f9" : "#0f172a",
  },
  studentListItem: {
    marginBottom: 14,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#f1f5f9",
  },
  metaLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: dk ? "#64748b" : "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },

  // ─── Roster Section ────────────────────────────────────────────────────────
  rosterCard: {
    marginTop: 24,
    padding: 20,
    borderRadius: 12,
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 8,
    elevation: 3,
  },
  rosterHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    flexWrap: "wrap",
    gap: 12,
  },
  sharedPasswordBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    backgroundColor: dk ? "rgba(14,165,233,0.15)" : "#f0f9ff",
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: dk ? "rgba(56,189,248,0.3)" : "#bae6fd",
  },
  sharedPasswordLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: dk ? "#60a5fa" : "#0284c7",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  sharedPasswordValue: {
    fontSize: 12,
    fontWeight: "600",
    color: dk ? "#60a5fa" : "#0369a1",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },

  // ─── Search Row ───────────────────────────────────────────────────────────
  searchRow: {
    marginBottom: 16,
  },
  searchInput: {
    width: "100%",
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#d1d5db",
    color: dk ? "#f1f5f9" : "#1e293b",
    backgroundColor: dk ? "#0f172a" : "#f9fafb",
    fontSize: 14,
  },
  emptyText: {
    fontSize: 14,
    color: dk ? "#64748b" : "#94a3b8",
    textAlign: "center",
    paddingVertical: 24,
  },

  // ─── Student Card (in roster) ──────────────────────────────────────────────
  studentCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    flexWrap: "wrap",
    padding: 16,
    marginBottom: 12,
    borderRadius: 10,
    backgroundColor: dk ? "#0f172a" : "#f8fafc",
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#e2e8f0",
    gap: 12,
  },
  studentCardLeft: {
    flex: 1,
    minWidth: 200,
  },
  studentCardName: {
    fontSize: 15,
    fontWeight: "700",
    color: dk ? "#f1f5f9" : "#0f172a",
    marginBottom: 6,
  },
  studentCardMeta: {
    fontSize: 12,
    color: dk ? "#94a3b8" : "#64748b",
    marginBottom: 2,
  },
  studentCardEmail: {
    fontSize: 13,
    color: dk ? "#94a3b8" : "#475569",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
    marginBottom: 4,
  },
  emailLink: {
    color: dk ? "#60a5fa" : "#0077b6",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },
  studentCardRight: {
    alignItems: "flex-end",
    minWidth: 80,
  },
  statusLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: dk ? "#64748b" : "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    marginBottom: 4,
  },
  statusValue: {
    fontSize: 12,
    fontWeight: "600",
    color: "#10b981",
  },
  studentCardActions: {
    flexDirection: "row",
    gap: 8,
    marginTop: 4,
  },
  actionBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
  },
  actionBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#ffffff",
  },
  actionBtnDanger: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 6,
    backgroundColor: dk ? "rgba(239,68,68,0.15)" : "#fee2e2",
    borderWidth: 1,
    borderColor: dk ? "rgba(248,113,113,0.3)" : "#fecaca",
  },
  actionBtnTextDanger: {
    fontSize: 12,
    fontWeight: "600",
    color: dk ? "#f87171" : "#dc2626",
  },

  // ─── Add Existing Button ──────────────────────────────────────────────────
  addExistingBtn: {
    marginTop: 12,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    alignSelf: "flex-start",
  },
  addExistingBtnText: {
    fontSize: 14,
    fontWeight: "600",
    color: "#ffffff",
  },

  // ─── Pills & Small Buttons ─────────────────────────────────────────────────
  smallPillButton: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
  },
  smallPillButtonDanger: {
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 6,
    backgroundColor: "#ef4444",
  },
  smallPillButtonText: {
    fontSize: 11,
    fontWeight: "600",
    color: "#ffffff",
  },
  smallActionBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: dk ? "#60a5fa" : "#0077b6",
    justifyContent: "center",
    alignItems: "center",
  },
  smallActionText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#ffffff",
  },

  // ─── Password Section ──────────────────────────────────────────────────────
  passwordSection: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 12,
    borderRadius: 8,
    backgroundColor: dk ? "#0f172a" : "#f1f5f9",
    marginBottom: 12,
    flexWrap: "wrap",
    gap: 8,
  },
  passwordLabel: {
    fontSize: 12,
    color: dk ? "#94a3b8" : "#64748b",
    fontWeight: "500",
  },
  passwordValue: {
    fontSize: 13,
    color: dk ? "#60a5fa" : "#0077b6",
    fontWeight: "700",
    fontFamily: Platform.OS === "web" ? "monospace" : undefined,
  },

  // ─── Add Existing Student ──────────────────────────────────────────────────
  addExistingRow: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  addExistingInput: {
    flex: 1,
    minWidth: 150,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: dk ? "rgba(148,163,184,0.15)" : "#d1d5db",
    color: dk ? "#f1f5f9" : "#1e293b",
    backgroundColor: dk ? "#0f172a" : "#f9fafb",
    fontSize: 14,
  },

  // ─── Modal Styles ──────────────────────────────────────────────────────────
  modalOverlay: {
    position: Platform.OS === "web" ? "fixed" : "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: dk ? "rgba(0, 0, 0, 0.7)" : "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    elevation: 1000,
    padding: 16,
  },
  modalCard: {
    width: "100%",
    maxWidth: 400,
    padding: 20,
    borderRadius: 16,
    backgroundColor: dk ? "#1e293b" : "#ffffff",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: dk ? 0.3 : 0.15,
    shadowRadius: 24,
    elevation: 12,
    zIndex: 1001,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: dk ? "#f1f5f9" : "#1e293b",
    marginBottom: 4,
  },
  modalSubtitle: {
    fontSize: 13,
    color: dk ? "#94a3b8" : "#64748b",
    marginBottom: 16,
    lineHeight: 18,
  },
  inputError: {
    borderColor: "#ef4444",
    backgroundColor: dk ? "rgba(239,68,68,0.15)" : "#fef2f2",
  },
  errorText: {
    color: "#ef4444",
    fontSize: 12,
    fontWeight: "500",
    marginBottom: 10,
  },
  passwordInputWrapper: {
    position: "relative",
    width: "100%",
    marginBottom: 16,
  },
  passwordInput: {
    paddingRight: 44,
  },
  eyeButton: {
    position: "absolute",
    right: 10,
    top: "50%",
    transform: [{ translateY: -12 }],
    padding: 6,
  },
eyeIcon: {
  fontSize: 16,
  color: dk ? "#64748b" : "#9ca3af",
},
});
